import { useEffect } from "react";

/**
 * Per-page SEO component (no react-helmet dependency — direct DOM mutation).
 * Updates <title>, <meta description>, <meta keywords>, canonical, OG, Twitter,
 * and injects a JSON-LD <script> block for the current route.
 *
 * Googlebot fully renders client-side React, so DOM-injected meta is indexed.
 */
const SITE = "https://abdullah-coffee-science.academy";
const DEFAULT_OG = `${SITE}/og-image.jpg`;

function setMeta(selector, attr, value) {
  if (!value) return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    const [name, val] = selector.replace(/[[\]"]/g, "").split("=");
    el.setAttribute(name, val);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

function setLink(rel, href, attrs = {}) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
}

function setJsonLd(id, data) {
  if (!data) return;
  let el = document.head.querySelector(`script[data-seo-id="${id}"]`);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-seo-id", id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export default function Seo({
  title,
  description,
  keywords,
  path = "",
  image = DEFAULT_OG,
  type = "website",
  jsonLd,
  jsonLdId = "page-jsonld",
  noindex = false,
}) {
  useEffect(() => {
    const url = `${SITE}${path || ""}`;
    const fullTitle = title
      ? `${title} | عبدالله | علم القهوة`
      : "عبدالله | علم القهوة — Abdullah Coffee Science Academy";

    document.title = fullTitle;
    setMeta('meta[name="description"]', "content", description);
    if (keywords) setMeta('meta[name="keywords"]', "content", keywords);
    setMeta('meta[name="robots"]', "content", noindex ? "noindex, nofollow" : "index, follow, max-snippet:-1, max-image-preview:large");

    // Canonical + hreflang
    setLink("canonical", url);

    // Open Graph
    setMeta('meta[property="og:title"]', "content", fullTitle);
    setMeta('meta[property="og:description"]', "content", description);
    setMeta('meta[property="og:url"]', "content", url);
    setMeta('meta[property="og:image"]', "content", image);
    setMeta('meta[property="og:type"]', "content", type);

    // Twitter
    setMeta('meta[name="twitter:title"]', "content", fullTitle);
    setMeta('meta[name="twitter:description"]', "content", description);
    setMeta('meta[name="twitter:image"]', "content", image);
    setMeta('meta[name="twitter:card"]', "content", "summary_large_image");

    // Per-page JSON-LD
    setJsonLd(jsonLdId, jsonLd);

    return () => {
      // Clean up only the per-page JSON-LD on unmount (keep base meta from index.html intact)
      const el = document.head.querySelector(`script[data-seo-id="${jsonLdId}"]`);
      if (el) el.remove();
    };
  }, [title, description, keywords, path, image, type, jsonLd, jsonLdId, noindex]);

  return null;
}
